import { stripInlineReplyTagsForDisplay } from "../utils/directive-tags.js";

const ENVELOPE_PREFIX = /^\[([^\]]+)\]\s*/;
const ENVELOPE_CHANNELS = [
  "WebChat",
  "WhatsApp",
  "Telegram",
  "Signal",
  "Slack",
  "Discord",
  "Google Chat",
  "iMessage",
  "Teams",
  "Matrix",
  "Zalo",
  "Zalo Personal",
  "BlueBubbles",
];

const MESSAGE_ID_LINE = /^\s*\[message_id:\s*[^\]]+\]\s*$/i;

/**
 * Recognised "body" line shapes inside a Quantum Palace continuity block.
 * The preflight injects either a `[memory recall] …` digest
 * (see `formatRecallDigest` in src/agents/iron-loop/hooks-default.ts) or
 * a project-context block (see `formatProjectBlock` in
 * src/memory/project-context.ts), with the two sometimes stacked.
 * Anything else is a terminator and starts the user message.
 */
const PALACE_DIGEST_HEADER_RE = /^\[memory recall\]/;
const PALACE_DIGEST_VERIFY_RE = /^Recalled snippets are hypotheses/;
const PALACE_DIGEST_TRUNCATED_RE = /^\[truncated;\s+\d+\s+more\]/;
// Legacy header for the prior preflight format — see `summarizeResults`
// in src/memory/quantum-palace/reasoning-bridge.ts. Kept here so
// messages persisted before the `[memory recall]` rename still strip
// cleanly.
const PALACE_LEGACY_HEADER_RE = /^Prior session context \(\d+ hits?\):\s*$/;
const PALACE_NUMBERED_HIT_RE = /^\d+\.\s+/;
const PALACE_PROJECT_HEADER_RE = /^##\s+Project context/;
const PALACE_PROJECT_BULLET_RE = /^-\s+\S/;
const PALACE_HTML_COMMENT_RE = /^<!--/;
const PALACE_BLANK_LINE = /^\s*$/;

function isPalaceBodyLine(line: string): boolean {
  if (PALACE_BLANK_LINE.test(line)) return true;
  if (PALACE_DIGEST_HEADER_RE.test(line)) return true;
  if (PALACE_DIGEST_VERIFY_RE.test(line)) return true;
  if (PALACE_DIGEST_TRUNCATED_RE.test(line)) return true;
  if (PALACE_LEGACY_HEADER_RE.test(line)) return true;
  if (PALACE_NUMBERED_HIT_RE.test(line)) return true;
  if (PALACE_PROJECT_HEADER_RE.test(line)) return true;
  if (PALACE_PROJECT_BULLET_RE.test(line)) return true;
  if (PALACE_HTML_COMMENT_RE.test(line)) return true;
  return false;
}

/**
 * Quantum Palace continuity blocks are prepended to the user prompt by
 * the preflight (see `formatPalaceContinuityUserBlock` in
 * src/orchestration/quantum-palace-poweron.ts) and MUST be stripped before
 * the bubble is rendered in the chat. The previous regex required the
 * body to terminate with a blank line (`\r?\n\r?\n`); when the block had
 * no trailing blank line — e.g. the palace block was the entire user
 * message (empty user input after the digest), or the gateway concatenated
 * the digest with a single `\n` separator instead of `\n\n` — the regex
 * failed to match and the full `[Quantum Palace …]\n<digest>` prefix
 * leaked into the user bubble as visible text. Walk the body line by line
 * and stop at the first non-palace-body line; the rest is the user
 * message. Handles all three terminator shapes (blank line, single
 * newline, end of string) without ambiguity because palace body content
 * is structured — `[memory recall]` headers, numbered `1. …` lines,
 * project-context bullets — and looks nothing like a user chat turn.
 */
export function stripQuantumPalaceContext(text: string): string {
  if (
    !text.startsWith("[Prior session context from Quantum Palace]") &&
    !text.startsWith("[Quantum Palace session continuity]")
  ) {
    return text;
  }
  const lines = text.split("\n");
  // Line 0 is the header — it cannot be body content, so start the body
  // walk at line 1.
  let i = 1;
  while (i < lines.length && isPalaceBodyLine(lines[i] ?? "")) {
    i += 1;
  }
  if (i === 1) {
    // Header with no body at all — there is nothing meaningful to strip
    // beyond the header line. Return the original text so the caller can
    // decide (typically: surface the unparseable block instead of
    // dropping the entire user message).
    return text;
  }
  return lines.slice(i).join("\n");
}

function looksLikeEnvelopeHeader(header: string): boolean {
  if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z\b/.test(header)) {
    return true;
  }
  if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}\b/.test(header)) {
    return true;
  }
  return ENVELOPE_CHANNELS.some((label) => header.startsWith(`${label} `));
}

export function stripEnvelope(text: string): string {
  const match = text.match(ENVELOPE_PREFIX);
  if (!match) {
    return text;
  }
  const header = match[1] ?? "";
  if (!looksLikeEnvelopeHeader(header)) {
    return text;
  }
  return text.slice(match[0].length);
}

export function stripSystemEventsPrefix(text: string): string {
  const lines = text.split(/\r?\n/);
  let start = 0;
  while (start < lines.length && lines[start]?.startsWith("System: ")) {
    start += 1;
  }
  while (start < lines.length && lines[start]?.trim() === "") {
    start += 1;
  }
  if (start === 0) {
    return text;
  }
  return lines.slice(start).join("\n");
}

function stripUserDisplayHints(text: string): string {
  let next = stripQuantumPalaceContext(text);
  next = stripSystemEventsPrefix(next);
  if (next.includes("[message_id:")) {
    const lines = next.split(/\r?\n/);
    const filtered = lines.filter((line) => !MESSAGE_ID_LINE.test(line));
    if (filtered.length !== lines.length) {
      next = filtered.join("\n");
    }
  }
  return stripEnvelope(next);
}

function stripEnvelopeFromContent(content: unknown[]): { content: unknown[]; changed: boolean } {
  let changed = false;
  const next = content.map((item) => {
    if (!item || typeof item !== "object") {
      return item;
    }
    const entry = item as Record<string, unknown>;
    if (entry.type !== "text" || typeof entry.text !== "string") {
      return item;
    }
    const stripped = stripUserDisplayHints(entry.text);
    if (stripped === entry.text) {
      return item;
    }
    changed = true;
    return {
      ...entry,
      text: stripped,
    };
  });
  return { content: next, changed };
}

function stripAssistantDisplayHints(text: string): string {
  return stripInlineReplyTagsForDisplay(stripAssistantEnvelope(text));
}

function stripAssistantDirectivesFromContent(content: unknown[]): {
  content: unknown[];
  changed: boolean;
} {
  let changed = false;
  const next = content.map((item) => {
    if (!item || typeof item !== "object") {
      return item;
    }
    const entry = item as Record<string, unknown>;
    if (entry.type !== "text" || typeof entry.text !== "string") {
      return item;
    }
    const stripped = stripAssistantDisplayHints(entry.text);
    if (stripped === entry.text) {
      return item;
    }
    changed = true;
    return {
      ...entry,
      text: stripped,
    };
  });
  return { content: next, changed };
}

/**
 * Strip an echoed timestamp envelope from an ASSISTANT message.
 *
 * Models occasionally copy the inbound envelope shape they were shown
 * (`[Wed 2026-10-14 14:37 UTC] — Welcome!`) and emit it as their own prefix.
 * Assistant rows are not envelope-stripped on the normal path, so the raw
 * stamp leaked into the bubble. Deliberately narrow: only a timestamp-shaped
 * header qualifies, and only a single following ` — ` is consumed — a
 * legitimate reply opening with `[note] …` is left untouched.
 */
export function stripAssistantEnvelope(text: string): string {
  const stripped = stripEnvelope(text);
  if (stripped === text) {
    return text;
  }
  return stripped.replace(/^—\s*/, "");
}

export function stripEnvelopeFromMessage(message: unknown): unknown {
  if (!message || typeof message !== "object") {
    return message;
  }
  const entry = message as Record<string, unknown>;
  const role = typeof entry.role === "string" ? entry.role.toLowerCase() : "";
  if (role === "assistant") {
    let changed = false;
    const next: Record<string, unknown> = { ...entry };
    if (typeof entry.content === "string") {
      const stripped = stripAssistantDisplayHints(entry.content);
      if (stripped !== entry.content) {
        next.content = stripped;
        changed = true;
      }
    } else if (Array.isArray(entry.content)) {
      const updated = stripAssistantDirectivesFromContent(entry.content);
      if (updated.changed) {
        next.content = updated.content;
        changed = true;
      }
    } else if (typeof entry.text === "string") {
      const stripped = stripAssistantDisplayHints(entry.text);
      if (stripped !== entry.text) {
        next.text = stripped;
        changed = true;
      }
    }
    return changed ? next : message;
  }
  if (role !== "user") {
    return message;
  }

  let changed = false;
  const next: Record<string, unknown> = { ...entry };

  if (typeof entry.content === "string") {
    const stripped = stripUserDisplayHints(entry.content);
    if (stripped !== entry.content) {
      next.content = stripped;
      changed = true;
    }
  } else if (Array.isArray(entry.content)) {
    const updated = stripEnvelopeFromContent(entry.content);
    if (updated.changed) {
      next.content = updated.content;
      changed = true;
    }
  } else if (typeof entry.text === "string") {
    const stripped = stripUserDisplayHints(entry.text);
    if (stripped !== entry.text) {
      next.text = stripped;
      changed = true;
    }
  }

  return changed ? next : message;
}

export function stripEnvelopeFromMessages(messages: unknown[]): unknown[] {
  if (messages.length === 0) {
    return messages;
  }
  let changed = false;
  const next = messages.map((message) => {
    const stripped = stripEnvelopeFromMessage(message);
    if (stripped !== message) {
      changed = true;
    }
    return stripped;
  });
  return changed ? next : messages;
}

const MESSAGE_ID_CAPTURE = /\[message_id:\s*([^\]]+)\]/i;

function extractMessageText(message: unknown): string {
  if (!message || typeof message !== "object") {
    return "";
  }
  const entry = message as Record<string, unknown>;
  if (typeof entry.content === "string") {
    return entry.content;
  }
  if (typeof entry.text === "string") {
    return entry.text;
  }
  if (!Array.isArray(entry.content)) {
    return "";
  }
  const parts: string[] = [];
  for (const item of entry.content) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const block = item as Record<string, unknown>;
    if (block.type === "text" && typeof block.text === "string") {
      parts.push(block.text);
    }
  }
  return parts.join("\n");
}

function extractReferencedMessageId(message: unknown): string | null {
  const match = extractMessageText(message).match(MESSAGE_ID_CAPTURE);
  const id = match?.[1]?.trim();
  return id && id.length > 0 ? id : null;
}

export function extractTranscriptMessageId(message: unknown): string | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const entry = message as Record<string, unknown>;
  for (const key of ["id", "transcriptId"] as const) {
    const value = entry[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function withTranscriptMessageId(message: unknown, id: string): unknown {
  if (!message || typeof message !== "object") {
    return message;
  }
  const entry = message as Record<string, unknown>;
  if (typeof entry.id === "string" && entry.id.trim() === id) {
    return message;
  }
  return { ...entry, id };
}

/**
 * Drop Pi `prompt()` echoes that duplicate a gateway-persisted user turn.
 * chat.send writes the raw user message (id = client runId); the embedded
 * runner then appends an enveloped copy containing `[message_id: <runId>]`.
 * Flat history reads show both — strip the echo so the UI shows one bubble.
 *
 * When the gateway row is missing (persist race / delay-until-assistant), the
 * echo is the only transcript copy. Rewrite its id to the referenced runId so
 * the control UI can reconcile against the optimistic bubble instead of
 * appending a second plain-text user message once streaming starts.
 */
export function dedupePromptEchoUserMessages(messages: unknown[]): unknown[] {
  if (messages.length === 0) {
    return messages;
  }
  const knownIds = new Set<string>();
  for (const message of messages) {
    const id = extractTranscriptMessageId(message);
    if (id) {
      knownIds.add(id);
    }
  }
  let changed = false;
  const next: unknown[] = [];
  for (const message of messages) {
    if (!message || typeof message !== "object") {
      next.push(message);
      continue;
    }
    const role =
      typeof (message as { role?: unknown }).role === "string"
        ? String((message as { role: string }).role).toLowerCase()
        : "";
    if (role !== "user") {
      next.push(message);
      continue;
    }
    const referenced = extractReferencedMessageId(message);
    if (!referenced) {
      next.push(message);
      continue;
    }
    const selfId = extractTranscriptMessageId(message);
    if (selfId && selfId === referenced) {
      next.push(message);
      continue;
    }
    if (knownIds.has(referenced)) {
      changed = true;
      continue;
    }
    // Gateway user row absent — promote the echo onto the client runId so
    // chat.history reconciles with the optimistic send (same id).
    const promoted = withTranscriptMessageId(message, referenced);
    if (promoted !== message) {
      changed = true;
      knownIds.add(referenced);
    }
    next.push(promoted);
  }
  return changed ? next : messages;
}
